import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Page, Layout, Text, SelectValue, Button, ReloadIcon} from '@phonectas/unifon-ui-kit';
import SearchWidget from 'common/components/SearchWidget';
import NewMrcTemplateSelect from './components/NewMrcSelect';
import AddNewMrcDialog from './components/AddNewMrcDialog';
import MrcCustomTable from './components/MrcTable';
import CustomLoader from 'common/components/customLoader';
import useMrcStore from 'common/stores/mrcStore';
import {shallow} from 'zustand/shallow';
import {useMrcList} from 'common/hooks/useMrcList';
import {debounce} from 'lodash';
import MrcExpandedRow from './components/MrcTable/MrcExpandedRow';
import {MrcEntryCreateRequest, MrcItem} from 'common/types/store/mrcTypes';
import {toMrcApiFormat} from './components/util';
import getMrcColumns from './components/MrcTable/getMrcColumns';
import MrcFilters from './components/MrcFilters';

const DEAFAULT_FILTERS = {
	mrc_is_failed: false,
	mrc_is_rejected: false,
	mrc_is_sent: false,
	mrc_is_signed: false,
	sms_is_failed: false,
	sms_is_sent: false,
};

const MrcDashboard: React.FC = () => {
	const [t] = useTranslation();
	const {
		fetchMrcList,
		resetQueryResult,
		resetActiveBrregOrg,
		createMrcEntry,
	} = useMrcStore();
	const {
		isLoading,
		mrcList,
	} = useMrcStore((state) => ({
		isLoading: state?.isLoading,
		mrcList: state?.list,
		totalCount: state?.list?.total_count,
		activeMrcRow: state?.activeRow,
	}), shallow);
	const [query, setQuery] = useState('');
	const [template, setTemplate] = useState<number | ''>('');
	const [showAddNewMrcDialog, setShowAddNewMrcDialog] = useState(false);
	const [
		shouldCloseExpandedRows,
		setShouldCloseExpandedRows,
	] = useState(false);
	const [filters, setFilters] = useState(DEAFAULT_FILTERS);
	const mrcListData = useMrcList(query);
	const columns = useMemo(() => getMrcColumns(t), [t]);

	// Helper function to get only active filters (true values)
	const getActiveFilters = useCallback(() => {
		return Object.entries(filters).reduce((acc, [key, value]) => {
			if (value === true) {
				acc[key] = value;
			}
			return acc;
		}, {} as Record<string, boolean>);
	}, [filters]);

	const handleFilterChange = useCallback((name: string, value: boolean) => {
		setFilters((prev) => ({
			...prev,
			[name]: value,
		}));
	}, []);

	const handleSendNewMrc = useCallback(async (
		templateId: number | string,
		formData: Partial<MrcEntryCreateRequest>,
	) => {
		const payload = toMrcApiFormat(formData, templateId) as MrcEntryCreateRequest
		await createMrcEntry(payload, () => {
			setTimeout(() => {
				fetchMrcList({limit: 50, offset: 0, search: query, ...getActiveFilters()});
				setShowAddNewMrcDialog(false);
				setTemplate('');
				resetActiveBrregOrg();
			}, 500);
		});
	}, [createMrcEntry, fetchMrcList, query, resetActiveBrregOrg, getActiveFilters]);

	const debouncedSearch = useMemo(() => debounce((searchValue) => {
		fetchMrcList({limit: 50, offset: 0, search: searchValue});
	}, 500), []);

	const handleLoadMore = useCallback(() => {
		if (isLoading) return;
		const totalCount = mrcList?.total_count ?? 0;
		const currentLength = mrcList?.mrcs?.length ?? 0;
		
		if (totalCount <= currentLength) return;

		fetchMrcList({
			limit: currentLength + 50,
			offset: 0,
			search: query,
			...filters,
		});
	}, [isLoading, mrcList, query, fetchMrcList, filters]);

	const handleCloseAddNewMrcDialog = useCallback(() => {
		setShowAddNewMrcDialog(false);
		resetActiveBrregOrg();
		setTemplate('');
	}, [resetActiveBrregOrg]);

	const handleTemplateChange = useCallback((value: SelectValue) => {
		if (typeof value === 'number' || typeof value === 'string') {
			setTemplate(Number(value));
			setShowAddNewMrcDialog(true);
		} else {
			setTemplate('');
		}
	}, []);

	const handleRefreshTable = useCallback(() => {
		fetchMrcList({limit: 50, offset: 0, search: query, ...getActiveFilters()});
	}, [fetchMrcList, query, getActiveFilters]);

	const handleSearchChange = useCallback((newQuery: string) => {
		setQuery(newQuery);
		
		// Only search if query has at least 4 digits or is empty (to clear search)
		if (newQuery.length === 0 || newQuery.length >= 4) {
			debouncedSearch(newQuery);
		}

		setShouldCloseExpandedRows(true);
		setTimeout(() => setShouldCloseExpandedRows(false), 100);
	}, [debouncedSearch]);

	const resetSearch = useCallback(() => {
		setQuery('');
		resetQueryResult();
	}, [resetQueryResult]);

	const onRowClick = useCallback((row: MrcItem) => {
		//do nothing for now
		const use = false;
		if (use) {
			console.log('Row clicked:', row);
		}
	}, []);

	useEffect(() => {
		// Whenever filters change, refetch the list (including initial mount)
		const activeFilters = getActiveFilters();
		fetchMrcList({limit: 50, offset: 0, search: query, ...activeFilters});
		setShouldCloseExpandedRows(true);
		setTimeout(() => setShouldCloseExpandedRows(false), 100);
	}, [filters, fetchMrcList, query]);

	return (
		<Page style={{overflow: 'hidden'}}>
			<Layout style={{rowGap: '5px'}} direction="column" fullWidth>
				<Layout fullWidth alignItems="center" nowrap style={{height: '40px'}}>
					<Text style={{fontSize: '30px'}} variant="h1">MRC</Text>
				</Layout>
			</Layout>
			<Layout
				fullWidth
				direction="column"
				fullHeight
				style={{marginTop: '20px', overflow: 'hidden'}}
			>
				<Layout fullWidth style={{background: '#fff', padding: '10px 12px', borderRadius: '8px'}}>
					<Layout
						direction="row"
						fullWidth
						alignItems="center"
						justifyContent="space-between"
						style={{height: '100%'}}
					>
						<Layout style={{width: '350px'}}>
							<SearchWidget
								placeholder={t('Search for numbers')}
								value={query}
								onChange={handleSearchChange}
								clearSearch={resetSearch}
								fullWidth
							/>
						</Layout>
						<Layout gap="xl">
							<Button
								icon={<ReloadIcon />}
								variant="alt"
								onClick={handleRefreshTable}
							/>
							<NewMrcTemplateSelect
								onChange={handleTemplateChange}
							/>
						</Layout>
						<Layout fullWidth >
							<MrcFilters
								filters={filters}
								onFilterChange={handleFilterChange}
							/>
						</Layout>
					</Layout>
				</Layout>
				<Layout
					fullWidth
					style={{
						flex: 1,
						minHeight: 0,
						overflow: 'hidden',
						marginBottom: '65px',
					}}
				>
					<MrcCustomTable
						columns={columns}
						data={mrcListData?.mrcs || []}
						onLoadMore={handleLoadMore}
						isLoading={isLoading}
						totalCount={
							mrcList ? mrcList.total_count : 0
						}
						shouldCloseExpandedRows={shouldCloseExpandedRows}
						onRowClick={(row) => onRowClick(row)}
						onExpand={true}
						renderExpandedRow={(row) => <MrcExpandedRow row={row} />}
					/>
				</Layout>
				<AddNewMrcDialog
					show={showAddNewMrcDialog}
					onClose={handleCloseAddNewMrcDialog}
					onSend={handleSendNewMrc}
					templateId={template}
				/>
			</Layout>
			<CustomLoader
				isLoading={isLoading}
				advanced
			/>
		</Page>
	);
};

export default MrcDashboard;